import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Tabs, Tab } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import SFDateRangeSelector from "../components/SFDateRangeSelector";
import PharmacyHaloButton from "../components/Button";
import { phappsRequest } from "../services/apiService";
import {
  PHARMACY_HALO_COLORS,
  DOCLINK_COLORS,
  QUDA_COLORS,
} from "../config/colors";
import { API_PATHS } from "../config/apiConstants";
import QudaAnalytics from "../components/QudaAnalytics";
import DocLinkAnalytics from "../components/DocLinkAnalytics";

const ChemistAnalytics = () => {
  const { chemistID } = useParams();
  const navigate = useNavigate();
  const [chemistData, setChemistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  // Initialize date range: last 30 days
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - 30);
    return [startDate, today];
  });

  useEffect(() => {
    const fetchChemistData = async () => {
      try {
        setLoading(true);

        const [startDate, endDate] = dateRange;

        const params = {
          PHUUID: chemistID,
          DateStart: startDate.toISOString().split("T")[0],
          DateEnd: endDate.toISOString().split("T")[0],
        };

        const response = await phappsRequest(
          API_PATHS.CHEMIST_DATA,
          "GET",
          null,
          params
        );
        if (response && response.ChemistData) {
          setChemistData(response);
        } else {
          setError("Failed to fetch chemist data");
        }
      } catch (err) {
        console.error("Error fetching chemist data:", err);
        setError("Error fetching chemist data");
      } finally {
        setLoading(false);
      }
    };

    if (chemistID) {
      fetchChemistData();
    }
  }, [chemistID, dateRange]);

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Determine the active color based on the selected tab
  const getActiveColor = () => {
    if (selectedTab === 0) return QUDA_COLORS.TEAL_MID;
    if (selectedTab === 1) return DOCLINK_COLORS.MID_PURPLE;
    return PHARMACY_HALO_COLORS.BLUE;
  };

  // Determine the background color based on the selected tab
  const getBackgroundColor = () => {
    if (selectedTab === 0) return `${QUDA_COLORS.TEAL_LIGHT}40`; // Increased opacity (~25%)
    if (selectedTab === 1) return `${DOCLINK_COLORS.LIGHT_PURPLE}40`;
    return "transparent";
  };

  if (loading) {
    return (
      <Box className="flex items-center justify-center h-64">
        <Typography variant="h6" color="textSecondary">
          Loading chemist data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex flex-col items-center justify-center h-64 space-y-4">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <PharmacyHaloButton onClick={() => navigate("/overview")}>
          <ArrowBack sx={{ mr: 1 }} />
          Back to Overview
        </PharmacyHaloButton>
      </Box>
    );
  }

  if (!chemistData || !chemistData.ChemistData) {
    return (
      <Box className="flex flex-col items-center justify-center h-64 space-y-4">
        <Typography variant="h6" color="textSecondary">
          No chemist data available
        </Typography>
        <PharmacyHaloButton onClick={() => navigate("/overview")}>
          <ArrowBack sx={{ mr: 1 }} />
          Back to Overview
        </PharmacyHaloButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: getBackgroundColor(),
        transition: "background-color 0.3s ease",
        p: 3,
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        // Extend background to edges by counteracting parent padding
        mx: -2,
        my: -2,
        px: 4,
        py: 4,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "1fr auto 1fr" },
          gap: 2,
          alignItems: "center",
        }}
      >
        {/* Left: Back Button */}
        <Box sx={{ justifySelf: { xs: "center", xl: "start" } }}>
          <PharmacyHaloButton onClick={() => navigate("/overview")}>
            <ArrowBack sx={{ mr: 1 }} />
            Back to Overview
          </PharmacyHaloButton>
        </Box>

        {/* Center: Pharmacy Name */}
        <Box sx={{ justifySelf: "center", textAlign: "center" }}>
          <Typography
            variant="h4"
            className="font-bold"
            style={{ color: PHARMACY_HALO_COLORS.BLUE }}
          >
            {chemistData.ChemistData.chemistName}
          </Typography>
        </Box>

        {/* Right: Date Range Selector */}
        <Box
          sx={{ justifySelf: { xs: "center", xl: "end" }, minWidth: "300px" }}
        >
          <SFDateRangeSelector
            value={dateRange}
            onChange={handleDateRangeChange}
          />
        </Box>
      </Box>

      {/* MUI Tabs */}
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            TabIndicatorProps={{
              style: {
                backgroundColor: getActiveColor(),
                height: "3px",
              },
            }}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: "normal",
                fontSize: "1rem",
                color: "#666",
              },
              "& .Mui-selected": {
                color: `${getActiveColor()} !important`,
                fontWeight: "bold",
              },
            }}
          >
            <Tab label="Quda Analytics" />
            <Tab label="DocLink Analytics" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ p: 2 }}>
          {selectedTab === 0 && (
            <QudaAnalytics
              chemistData={chemistData.ChemistData}
              chemistID={chemistID}
            />
          )}
          {selectedTab === 1 && (
            <DocLinkAnalytics chemistData={chemistData.ChemistData} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChemistAnalytics;
